import { useMemo } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding, useOnboardingOptional } from "@/contexts/OnboardingContext";

// Routes that are ALWAYS reachable during onboarding (no step required).
const ALWAYS_ALLOWED_PREFIXES = [
  "/getting-started",
  "/welcome",
  "/onboarding",
  /*
   * La cuenta del usuario, ENTERA. `/settings/my-account` es solo un índice: sus
   * filas llevan a `/settings/regional` (idioma), `/settings/reports` (avisos),
   * `/settings/legal`, `/settings/account` y `/settings/billing`. Con únicamente el
   * índice en la lista, entrar se podía pero tocar cualquier fila te devolvía a la
   * guía sin explicar nada — el idioma no había forma de cambiarlo mientras durase
   * la puesta en marcha, y es justo cuando alguien lo necesita: si la aplicación te
   * recibió en un idioma que no es el tuyo, no vas a poder completar los seis pasos.
   *
   * La puerta está para que nadie se salte la CONFIGURACIÓN DE LA EMPRESA
   * (inventario, plagas, planificador) antes de tiempo, no para secuestrar los
   * ajustes personales. Lo que no se abre aquí es `/settings` a secas ni el resto de
   * ajustes de empresa.
   */
  "/settings/my-account",
  "/settings/account",
  "/settings/regional",
  "/settings/reports",
  "/settings/legal",
  "/settings/billing",
  // Asistente geométrico (T5): el paso "structure" puede completarse desde /designer.
  "/designer",
];

// Step-route mapping. Order matters: enforced sequentially in the gate.
// Must mirror guide_steps.sort_order in DB.
const STEP_ROUTE_PREFIX: Record<string, string> = {
  structure: "/settings/structure",
  inventory: "/inventory",
  pests: "/pests",
  treatments: "/pests",
  team: "/teams",
  planner: "/planner",
};

function isUnderPrefix(pathname: string, prefix: string): boolean {
  if (pathname === prefix) return true;
  if (prefix !== "/" && pathname.startsWith(prefix + "/")) return true;
  return false;
}

export function useOnboardingGate() {
  const { role, isFirstAdmin, profile } = useAuth();
  // Sin provider (portales de perito y trabajador) la puerta está simplemente
  // cerrada: no hay guía que atender. Antes esto reventaba, y como lo llama la
  // ventana de perfil —compartida por los tres portales— tumbaba el portal del
  // perito entero al montarse. El resultado es el mismo que ya daba para
  // cualquiera que no sea el primer admin: `active: false`.
  const onboarding = useOnboardingOptional();
  const isComplete = onboarding?.isComplete ?? true;
  const isLoading = onboarding?.isLoading ?? false;

  return useMemo(() => {
    // Permanent escape hatch: once a user has truly finished onboarding,
    // never reopen the gate — even if they later delete all their data.
    const alreadyDone = profile?.has_completed_app_onboarding === true;
    const active =
      role === "admin" &&
      isFirstAdmin &&
      !alreadyDone &&
      !isLoading &&
      !isComplete;
    return { active };
  }, [role, isFirstAdmin, profile?.has_completed_app_onboarding, isLoading, isComplete]);
}

/**
 * Compute the next pending step's key from a steps list and completed keys.
 * Steps are assumed already sorted by sort_order.
 */
export function getNextPendingStepKey(
  steps: { step_key: string }[],
  completedSteps: string[]
): string | null {
  for (const s of steps) {
    if (!completedSteps.includes(s.step_key)) return s.step_key;
  }
  return null;
}

/**
 * Wraps page content. When the onboarding gate is active, only the route of
 * the CURRENT next step (plus always-allowed prefixes) is reachable. Every
 * other route is redirected to /getting-started.
 */
export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { active } = useOnboardingGate();
  const location = useLocation();
  const { steps, completedSteps } = useOnboarding();

  if (!active) return <>{children}</>;

  const path = location.pathname;

  if (ALWAYS_ALLOWED_PREFIXES.some((p) => isUnderPrefix(path, p))) {
    return <>{children}</>;
  }

  const nextKey = getNextPendingStepKey(steps, completedSteps);
  const nextPrefix = nextKey ? STEP_ROUTE_PREFIX[nextKey] : null;

  // Allow current next step's route
  if (nextPrefix && isUnderPrefix(path, nextPrefix)) {
    return <>{children}</>;
  }

  // Also allow access to any COMPLETED step's route for review
  const completedPrefixes = completedSteps
    .map((k) => STEP_ROUTE_PREFIX[k])
    .filter(Boolean);
  if (completedPrefixes.some((p) => isUnderPrefix(path, p!))) {
    return <>{children}</>;
  }

  return <Navigate to="/getting-started" replace />;
}
